import React, { useEffect, useRef, useState } from 'react';
import { useStaticQuery, graphql } from 'gatsby';
import { GatsbyImage, getImage } from 'gatsby-plugin-image';
import styled from 'styled-components';
import sr from '@utils/sr';
import { srConfig } from '@config';
import { Icon } from '@components/icons';
import { usePrefersReducedMotion } from '@hooks';

const StyledPublicationsGrid = styled.ul`
  ${({ theme }) => theme.mixins.resetList};
`;

const StyledPublication = styled.li`
  position: relative;
  display: grid;
  grid-template-columns: repeat(12, 1fr);
  grid-gap: 20px;
  align-items: center;
  margin-bottom: 100px;

  @media (max-width: 768px) {
    display: block;
    margin-bottom: 70px;
  }

  &:nth-of-type(odd) {
    .project-content {
      grid-column: 7 / -1;
      text-align: right;

      @media (max-width: 1080px) {
        grid-column: 5 / -1;
      }

      @media (max-width: 768px) {
        grid-column: 1 / -1;
        text-align: left;
      }
    }

    .project-image {
      grid-column: 1 / 7;

      @media (max-width: 768px) {
        grid-column: 1 / -1;
      }
    }

    .project-links {
      justify-content: flex-end;

      @media (max-width: 768px) {
        justify-content: flex-start;
      }
    }
  }

  &:nth-of-type(even) {
    .project-content {
      grid-column: 1 / 7;
      text-align: left;

      @media (max-width: 1080px) {
        grid-column: 1 / 9;
      }

      @media (max-width: 768px) {
        grid-column: 1 / -1;
        text-align: left;
      }
    }

    .project-image {
      grid-column: 7 / -1;

      @media (max-width: 768px) {
        grid-column: 1 / -1;
      }
    }

    .project-links {
      justify-content: flex-start;
    }
  }

  .project-content {
    grid-row: 1 / -1;
    margin-top: 20px;
    margin-bottom: 20px;
  }

  .project-overline {
    margin: 10px 0;
    color: var(--green);
    font-family: var(--font-sans);
    font-size: var(--fz-xs);
    font-weight: 500;
  }

  .project-title {
    background: linear-gradient(90deg, #e2e8f0, #cbd5e1);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    display: inline-block;
    font-size: clamp(24px, 5vw, 28px);
    margin: 0 0 20px;
  }

  .project-description {
    ${({ theme }) => theme.mixins.boxShadow};
    padding: 25px;
    border-radius: var(--border-radius);
    background-color: var(--light-navy);
    color: var(--light-slate);
    font-size: var(--fz-lg);
    margin-bottom: 15px;

    a {
      ${({ theme }) => theme.mixins.inlineLink};
    }
  }

  .project-links {
    display: flex;
    align-items: center;
    margin-left: -10px;

    a {
      ${({ theme }) => theme.mixins.flexCenter};
      padding: 10px;

      svg {
        width: 20px;
        height: 20px;
      }
    }
  }
`;

const StyledIframeWrapper = styled.div`
  position: relative;
  width: 100%;
  padding-bottom: 56.25%;
  height: 0;
  overflow: hidden;
  border-radius: var(--border-radius);
  background-color: var(--navy);

  iframe {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    border: none;
  }
`;

const ImageWrapper = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  max-height: 400px;
  overflow: hidden;
  border-radius: var(--border-radius);
  cursor: pointer;

  @media (max-width: 768px) {
    max-height: 250px;
  }

  .play-icon {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    background-color: rgba(10, 25, 47, 0.7);
    border-radius: 50%;
    padding: 15px;
    z-index: 2;

    svg {
      width: 40px;
      height: 40px;
      fill: var(--white);
    }

    &:hover {
      background-color: rgba(10, 25, 47, 0.9);
    }
  }
`;

const MorePublicationsButton = styled.button`
  display: block;
  margin: 0 auto 50px;
  background: none;
  border: 2px solid var(--green);
  color: var(--green);
  padding: 10px 20px;
  font-family: var(--font-sans);
  font-size: var(--fz-sm);
  font-weight: 500;
  border-radius: var(--border-radius);
  cursor: pointer;
  transition: var(--transition);

  &:hover,
  &:focus {
    background-color: var(--green-tint);
    outline: none;
  }
`;

const Publications = () => {
  const data = useStaticQuery(graphql`
    {
      publications: allMarkdownRemark(
        filter: { fileAbsolutePath: { regex: "/content/publications/" } }
        sort: { fields: [frontmatter___date], order: DESC }
      ) {
        edges {
          node {
            frontmatter {
              title
              short_title
              cover {
                childImageSharp {
                  gatsbyImageData(width: 1000, placeholder: NONE)
                }
              }
              github
              youtube
              arxiv
            }
            html
          }
        }
      }
    }
  `);

  const publications = data.publications.edges;
  const revealTitle = useRef(null);
  const revealItems = useRef([]);
  const prefersReducedMotion = usePrefersReducedMotion();
  const [activeVideo, setActiveVideo] = useState(null);
  const [showAll, setShowAll] = useState(false);

  useEffect(() => {
    if (prefersReducedMotion) return;
    sr.reveal(revealTitle.current, srConfig());
    revealItems.current.forEach((ref, i) => sr.reveal(ref, srConfig(i * 100)));
  }, []);

  const visiblePublications = showAll ? publications : publications.slice(0, 3);

  return (
    <section id="publications">
      <h2 className="gradient-text text-xl" ref={revealTitle}>Recent Publications</h2>

      <StyledPublicationsGrid>
        {visiblePublications.map(({ node }, i) => {
          const { frontmatter, html } = node;
          const { title, short_title, cover, github, youtube, arxiv } = frontmatter;
          const image = getImage(cover);

          return (
            <StyledPublication key={i} ref={el => (revealItems.current[i] = el)}>
              <div className="project-content">
                <p className="project-overline">{title}</p>
                <h3 className="project-title">{short_title}</h3>
                <div className="project-description" dangerouslySetInnerHTML={{ __html: html }} />
                <div className="project-links">
                  {github && (
                    <a href={github} aria-label="GitHub Link">
                      <Icon name="GitHub" />
                    </a>
                  )}
                  {arxiv && (
                    <a href={arxiv} aria-label="arxiv PDF Link">
                      <Icon name="arxiv" />
                    </a>
                  )}
                </div>
              </div>

              <div className="project-image">
                {activeVideo === i ? (
                  <StyledIframeWrapper>
                    <iframe
                      src={`${youtube}${youtube.includes('?') ? '&' : '?'}autoplay=1`}
                      title={title}
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                    />
                  </StyledIframeWrapper>
                ) : (
                  <ImageWrapper onClick={() => setActiveVideo(i)}>
                    <span className="play-icon" aria-hidden="true">
                      <Icon name="Play" />
                    </span>
                    <GatsbyImage
                      image={image}
                      alt={title}
                      style={{ width: '100%', height: '100%' }}
                      imgStyle={{
                        objectFit: 'cover',
                        borderRadius: 'var(--border-radius)',
                        filter: 'none',
                        mixBlendMode: 'normal',
                      }}
                    />
                  </ImageWrapper>
                )}
              </div>
            </StyledPublication>
          );
        })}
      </StyledPublicationsGrid>

      {!showAll && publications.length > 3 && (
        <MorePublicationsButton onClick={() => setShowAll(true)}>
          More Publications
        </MorePublicationsButton>
      )}
    </section>
  );
};

export default Publications;
